import React, { useCallback, useState } from 'react';
import OutsideClickTracker from '../../../../../../components/outside-click-tracker';
import * as S from './styles';

type Props = {
  defaultValue: string;
  min: number;
  max: number;
  onChange: (value: number) => void;
  onClose: (value: number) => void;
  handleRef: any;
};

export const clamp = (value: number, min: number, max: number) =>
  isNaN(value) ? NaN : Math.max(min, Math.min(max, value));

const SliderInput = ({
  defaultValue,
  min,
  max,
  onChange,
  onClose,
  handleRef,
}: Props) => {
  const [value, setValue] = useState(defaultValue);
  const [isValid, setValid] = useState(!isNaN(parseFloat(defaultValue)));
  const close = useCallback(
    (newValue: string = '') => {
      const parsedValue = newValue !== '' ? parseFloat(newValue) : NaN;
      const result = !isNaN(parsedValue) ? clamp(parsedValue, min, max) : NaN;
      if (!isNaN(result)) {
        onChange(result);
      }
      onClose(result);
    },
    [max, min, onChange, onClose]
  );
  const update = useCallback(
    (newValue: string = '') => {
      const parsedValue = newValue !== '' ? parseFloat(newValue) : NaN;
      const valueIsValid = !isNaN(parsedValue);
      const valueIsWithinBounds =
        valueIsValid && parsedValue >= min && parsedValue <= max;
      setValid(valueIsValid && valueIsWithinBounds);
      setValue(newValue);
      if (valueIsValid && valueIsWithinBounds) {
        onChange(parsedValue);
      }
    },
    [max, min, onChange]
  );
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => update(e.currentTarget.value),
    [update]
  );
  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => close(e.currentTarget.value),
    [close]
  );
  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.keyCode) {
        case 13: {
          // enter
          close(e.currentTarget.value);
          break;
        }
        case 27: {
          // esc
          close();
          break;
        }
        default:
      }
    },
    [close]
  );
  const onOutsideClick = useCallback(() => close(value), [close, value]);
  return (
    <OutsideClickTracker onMouseDownOutside={onOutsideClick}>
      <S.SliderInput
        value={value}
        onChange={onInputChange}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        isValid={isValid}
        ref={handleRef}
        autoFocus
        data-test="SliderInput"
      />
    </OutsideClickTracker>
  );
};

export default SliderInput;
